import { UserRole, ValidationStatus, Severity, ProcurementType, FindingCategory } from '../types';

// API Endpoints
export const API_ENDPOINTS = {
  // Health
  HEALTH: '/health',

  // Evaluation
  EVALUATE: '/api/v1/evaluate',
  VALIDATION: '/api/v1/validations',
  VALIDATION_DETAIL: (id: string) => `/api/v1/validations/${id}`,

  // Export
  EXPORT: '/api/v1/export',

  // Statistics
  STATISTICS: '/api/v1/statistics',

  // User
  PROFILE: '/api/v1/user/profile',
} as const;

// User Roles - Azure AD Roles
export const USER_ROLES = {
  ProcurementOfficer: UserRole.ProcurementOfficer,
  AuthorityAdmin: UserRole.AuthorityAdmin,
  MmrOversight: UserRole.MmrOversight,
  SystemAdmin: UserRole.SystemAdmin,
} as const;

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.ProcurementOfficer]: 'Zadavatel',
  [UserRole.AuthorityAdmin]: 'Administrátor úřadu',
  [UserRole.MmrOversight]: 'Dohled MMR',
  [UserRole.SystemAdmin]: 'Systémový administrátor',
};

// Validation Status
export const VALIDATION_STATUSES = {
  PENDING: ValidationStatus.PENDING,
  PROCESSING: ValidationStatus.PROCESSING,
  COMPLETED: ValidationStatus.COMPLETED,
  FAILED: ValidationStatus.FAILED,
  CANCELLED: ValidationStatus.CANCELLED,
} as const;

export const VALIDATION_STATUS_LABELS: Record<ValidationStatus, string> = {
  [ValidationStatus.PENDING]: 'Čeká se',
  [ValidationStatus.PROCESSING]: 'Zpracovává se',
  [ValidationStatus.COMPLETED]: 'Dokončeno',
  [ValidationStatus.FAILED]: 'Selhalo',
  [ValidationStatus.CANCELLED]: 'Zrušeno',
};

export const VALIDATION_STATUS_COLORS: Record<ValidationStatus, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  [ValidationStatus.PENDING]: 'default',
  [ValidationStatus.PROCESSING]: 'info',
  [ValidationStatus.COMPLETED]: 'success',
  [ValidationStatus.FAILED]: 'error',
  [ValidationStatus.CANCELLED]: 'warning',
};

// Severity Levels
export const SEVERITY_LEVELS = {
  HIGH: Severity.HIGH,
  MEDIUM: Severity.MEDIUM,
  LOW: Severity.LOW,
  INFO: Severity.INFO,
} as const;

export const SEVERITY_LABELS: Record<Severity, string> = {
  [Severity.HIGH]: 'Vysoké',
  [Severity.MEDIUM]: 'Střední',
  [Severity.LOW]: 'Nízké',
  [Severity.INFO]: 'Informace',
};

export const SEVERITY_COLORS: Record<Severity, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  [Severity.HIGH]: 'error',
  [Severity.MEDIUM]: 'warning',
  [Severity.LOW]: 'info',
  [Severity.INFO]: 'default',
};

// Procurement Types
export const PROCUREMENT_TYPES = {
  OPEN: ProcurementType.OPEN,
  RESTRICTED: ProcurementType.RESTRICTED,
  NEGOTIATED: ProcurementType.NEGOTIATED,
  COMPETITIVE_DIALOGUE: ProcurementType.COMPETITIVE_DIALOGUE,
  INNOVATION_PARTNERSHIP: ProcurementType.INNOVATION_PARTNERSHIP,
  SIMPLIFIED_BELOW_THRESHOLD: ProcurementType.SIMPLIFIED_BELOW_THRESHOLD,
} as const;

export const PROCUREMENT_TYPE_LABELS: Record<ProcurementType, string> = {
  [ProcurementType.OPEN]: 'Otevřené řízení',
  [ProcurementType.RESTRICTED]: 'Užší řízení',
  [ProcurementType.NEGOTIATED]: 'Jednací řízení',
  [ProcurementType.COMPETITIVE_DIALOGUE]: 'Soutěžní dialog',
  [ProcurementType.INNOVATION_PARTNERSHIP]: 'Partnerství pro inovace',
  [ProcurementType.SIMPLIFIED_BELOW_THRESHOLD]: 'Zjednodušené podlimitní řízení',
};

// Finding Categories
export const FINDING_CATEGORIES = {
  FORMAL: FindingCategory.FORMAL,
  LEGAL: FindingCategory.LEGAL,
  FINANCIAL: FindingCategory.FINANCIAL,
  TECHNICAL: FindingCategory.TECHNICAL,
  PROCEDURAL: FindingCategory.PROCEDURAL,
  OTHER: FindingCategory.OTHER,
} as const;

export const FINDING_CATEGORY_LABELS: Record<FindingCategory, string> = {
  [FindingCategory.FORMAL]: 'Formální',
  [FindingCategory.LEGAL]: 'Právní',
  [FindingCategory.FINANCIAL]: 'Finanční',
  [FindingCategory.TECHNICAL]: 'Technické',
  [FindingCategory.PROCEDURAL]: 'Procesní',
  [FindingCategory.OTHER]: 'Ostatní',
};

// File Upload
export const FILE_UPLOAD = {
  MAX_SIZE_BYTES: 50 * 1024 * 1024, // 50 MB
  MAX_SIZE_MB: 50,
  ACCEPTED_TYPES: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/xml',
    'text/xml',
  ],
  ACCEPTED_EXTENSIONS: ['.pdf', '.docx', '.doc', '.xml'],
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
} as const;

// Date Formats
export const DATE_FORMATS = {
  DATE: 'dd.MM.yyyy',
  DATETIME: 'dd.MM.yyyy HH:mm',
  DATETIME_SECONDS: 'dd.MM.yyyy HH:mm:ss',
  TIME: 'HH:mm',
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  THEME: 'theme-storage',
  AUTH_TOKEN: 'auth-token',
  USER_PREFERENCES: 'user-preferences',
} as const;

// Navigation Routes
export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/',
  UPLOAD: '/upload',
  HISTORY: '/history',
  RESULTS: '/results/:id',
  RESULTS_PATH: (id: string) => `/results/${id}`,
  ADMIN: '/admin',
  PROFILE: '/profile',
  LOGIN: '/login',
  NOT_FOUND: '*',
} as const;

// Application Info
export const APP_INFO = {
  NAME: 'VZMR Checker',
  VERSION: '1.0.0',
  DESCRIPTION: 'Systém pro kontrolu zadávací dokumentace VZMR',
  COPYRIGHT: '© 2026 MMR ČR',
} as const;

// Validation Layers
export const VALIDATION_LAYERS = {
  FORMAL: 'Formální kontrola',
  LEGAL: 'Právní kontrola',
  FINANCIAL: 'Finanční kontrola',
  TECHNICAL: 'Technická kontrola',
  COMPLIANCE: 'Kontrola souladu',
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Chyba sítě. Zkontrolujte připojení k internetu.',
  SERVER_ERROR: 'Chyba serveru. Zkuste to prosím později.',
  UNAUTHORIZED: 'Nemáte oprávnění k této akci.',
  NOT_FOUND: 'Požadovaný zdroj nebyl nalezen.',
  VALIDATION_ERROR: 'Chyba validace. Zkontrolujte zadané údaje.',
  FILE_TOO_LARGE: `Soubor je příliš velký. Maximální velikost je ${FILE_UPLOAD.MAX_SIZE_MB} MB.`,
  INVALID_FILE_TYPE: 'Nepodporovaný typ souboru.',
  UPLOAD_FAILED: 'Nahrání souboru selhalo.',
  GENERIC_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  UPLOAD_SUCCESS: 'Soubor byl úspěšně nahrán.',
  VALIDATION_STARTED: 'Validace byla zahájena.',
  SAVE_SUCCESS: 'Změny byly úspěšně uloženy.',
  DELETE_SUCCESS: 'Položka byla úspěšně smazána.',
  EXPORT_SUCCESS: 'Export byl úspěšně dokončen.',
} as const;

// Timeouts and Intervals
export const TIMEOUTS = {
  DEBOUNCE: 300,
  TOAST_DURATION: 5000,
  POLLING_INTERVAL: 5000,
  REQUEST_TIMEOUT: 30000,
} as const;
